import { useState, useMemo, useCallback } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import {
    useGetSuperAdminUserListQuery,
    useGetManagerRoleClaimListQuery,
    useAddManagerRoleClaimMutation,
    useRemoveManagerRoleClaimMutation
} from '../../../../../api/endpoints/superAdminDefinition';

const DEFAULT_FORM_DATA = {
    userId: ''
};

export const useSuperAdminDefinition = () => {
    const { getCurrentLanguageId } = useLanguage();
    const currentLanguageId = getCurrentLanguageId();

    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [displayModal, setDisplayModal] = useState(false);
    const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

    const {
        data: listData,
        isLoading: isLoadingList,
        isFetching: isFetchingList,
        error
    } = useGetManagerRoleClaimListQuery(undefined, {
        refetchOnMountOrArgChange: true
    });

    const {
        data: userList,
        isLoading: isLoadingUsers
    } = useGetSuperAdminUserListQuery(
        { languageId: currentLanguageId },
        { refetchOnMountOrArgChange: true }
    );

    const [addManagerRoleClaim, { isLoading: isAdding }] = useAddManagerRoleClaimMutation();
    const [removeManagerRoleClaim, { isLoading: isRemoving }] = useRemoveManagerRoleClaimMutation();

    const items = useMemo(() => listData || [], [listData]);

    const assignedUserIds = useMemo(() => new Set(items.map((item) => item.id)), [items]);

    const availableUsers = useMemo(
        () => (userList || []).filter((user) => !assignedUserIds.has(user.id)),
        [userList, assignedUserIds]
    );

    const paginatedItems = useMemo(() => {
        const start = pageNumber * pageSize;
        return items.slice(start, start + pageSize);
    }, [items, pageNumber, pageSize]);

    const validateForm = useCallback(() => Boolean(formData.userId), [formData.userId]);

    const handleNew = useCallback(() => {
        setFormData(DEFAULT_FORM_DATA);
        setDisplayModal(true);
    }, []);

    const closeModal = useCallback(() => {
        setDisplayModal(false);
        setFormData(DEFAULT_FORM_DATA);
    }, []);

    const handleAdd = useCallback(async () => {
        await addManagerRoleClaim(formData.userId).unwrap();
        closeModal();
    }, [formData.userId, addManagerRoleClaim, closeModal]);

    const handleRemove = useCallback(
        async (item) => {
            await removeManagerRoleClaim(item.id).unwrap();
        },
        [removeManagerRoleClaim]
    );

    const handlePageChange = useCallback((event) => {
        setPageNumber(event.page);
        setPageSize(event.rows);
    }, []);

    return {
        items: paginatedItems,
        availableUsers,
        formData,
        setFormData,
        displayModal,
        isLoading: isLoadingList || isFetchingList,
        isLoadingUsers,
        isAdding,
        isRemoving,
        pageNumber,
        pageSize,
        totalRecords: items.length,
        validateForm,
        handleNew,
        handleAdd,
        handleRemove,
        handlePageChange,
        closeModal,
        error
    };
};
